import React, { useState, useEffect } from 'react';
import DashboardSidebar from './DashboardSidebar';
import { Menu } from 'lucide-react';

/**
 * SidebarLayout - Full responsive sidebar layout.
 *
 * Breakpoints:
 *   < 768px  (mobile)  → sidebar hidden, hamburger button in topbar overlay
 *   768-1024 (tablet)  → sidebar collapsed (72px) by default
 *   > 1024px (desktop) → sidebar expanded (260px) by default
 */
const SidebarLayout = ({ children, storageData }) => {
    const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
    const [isTablet, setIsTablet] = useState(() => window.innerWidth >= 768 && window.innerWidth < 1024);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(
        () => {
            const w = window.innerWidth;
            // On tablet, default to collapsed unless user explicitly expanded
            if (w >= 768 && w < 1024 && !localStorage.getItem('sidebar_collapsed')) {
                return true;
            }
            return localStorage.getItem('sidebar_collapsed') === 'true';
        }
    );

    useEffect(() => {
        const handleResize = () => {
            const w = window.innerWidth;
            const mobile = w < 768;
            const tablet = w >= 768 && w < 1024;
            setIsMobile(mobile);
            setIsTablet(tablet);
            // Auto-collapse on tablet if not already set
            if (tablet && !localStorage.getItem('sidebar_collapsed')) {
                setCollapsed(true);
            }
            // Close mobile drawer when resizing to desktop
            if (!mobile) {
                setMobileOpen(false);
            }
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleToggle = () => {
        if (isMobile) {
            setMobileOpen(prev => !prev);
        } else {
            setCollapsed(prev => {
                const next = !prev;
                localStorage.setItem('sidebar_collapsed', String(next));
                return next;
            });
        }
    };

    const handleCloseMobile = () => setMobileOpen(false);

    return (
        <div style={{
            display: 'flex',
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            position: 'relative',
        }}>
            {/* ── Mobile overlay backdrop ── */}
            {isMobile && mobileOpen && (
                <div
                    onClick={handleCloseMobile}
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0,0,0,0.5)',
                        zIndex: 40,
                        backdropFilter: 'blur(2px)',
                    }}
                />
            )}

            {/* ── Sidebar ── */}
            <div style={{
                // On mobile: fixed overlay sliding from left
                // On tablet/desktop: normal flow
                ...(isMobile ? {
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    height: '100vh',
                    zIndex: 50,
                    transform: mobileOpen ? 'translateX(0)' : 'translateX(-100%)',
                    transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1)',
                } : {
                    position: 'sticky',
                    top: 0,
                    height: '100vh',
                    flexShrink: 0,
                    zIndex: 30,
                }),
            }}>
                <DashboardSidebar
                    storageData={storageData}
                    collapsed={isMobile ? false : collapsed}
                    onToggle={isMobile ? handleCloseMobile : handleToggle}
                />
            </div>

            {/* ── Main Content ── */}
            <div style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                overflowX: 'hidden',
            }}>
                {/* Mobile top bar with hamburger */}
                {isMobile && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px 16px',
                        background: '#ffffff',
                        borderBottom: '1px solid #e2e8f0',
                        position: 'sticky',
                        top: 0,
                        zIndex: 20,
                        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                    }}>
                        <button
                            onClick={handleToggle}
                            style={{
                                background: 'rgba(15,23,42,0.08)',
                                border: 'none',
                                borderRadius: '8px',
                                width: '36px',
                                height: '36px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                color: '#334155',
                            }}
                        >
                            <Menu size={18} />
                        </button>
                        <span style={{ color: '#1e293b', fontWeight: 700, fontSize: '15px' }}>
                            Data Storage
                        </span>
                    </div>
                )}

                {/* Scrollable area */}
                <div style={{
                    flex: 1,
                    overflowY: 'auto',
                    padding: isMobile ? '16px' : '32px',
                }}>
                    {children}
                </div>
            </div>
        </div>
    );
};

export default SidebarLayout;
